import { Body, Button, Heading, IconButton } from '@assembly-js/design-system';
import MUIDialog from '@mui/material/Dialog';
import clsx from 'clsx';
import { ReactNode, useId } from 'react';

const MODAL_WIDTH_PX = {
  sm: 460,
  lg: 720,
} as const;
// On phones the modal caps here so it keeps a gutter on small screens.
const MOBILE_MAX_WIDTH_PX = 440;

const PAPER_SHADOW =
  '0 8px 24px rgba(14, 14, 16, 0.12), 0 2px 4px rgba(14, 14, 16, 0.06)';
const BACKDROP_COLOR = 'rgba(0, 0, 0, 0.3)';

interface GuidedModalProps {
  isOpen: boolean;
  title: string;
  description: string;
  /** Accessible label for the close button. */
  closeLabel: string;
  /** Close button, backdrop click or escape. */
  onClose: () => void;
  /** The footer's way out, e.g. "Maybe later". */
  dismissLabel: string;
  onDismiss: () => void;
  /** A footer action beside the dismiss button, which then turns into a text button. */
  primaryAction?: { label: string; onClick: () => void };
  size?: keyof typeof MODAL_WIDTH_PX;
  children: ReactNode;
}

/**
 * Onboarding modal shell: a title and one line of copy over the modal's own
 * content, with footer buttons that go full width on phones.
 */
export const GuidedModal = ({
  isOpen,
  title,
  description,
  closeLabel,
  onClose,
  dismissLabel,
  onDismiss,
  primaryAction,
  size = 'sm',
  children,
}: GuidedModalProps) => {
  const titleId = useId();

  return (
    <MUIDialog
      open={isOpen}
      onClose={onClose}
      aria-labelledby={titleId}
      maxWidth={false}
      slotProps={{ backdrop: { sx: { backgroundColor: BACKDROP_COLOR } } }}
      PaperProps={{
        sx: {
          width: '100%',
          maxWidth: { xs: MOBILE_MAX_WIDTH_PX, sm: MODAL_WIDTH_PX[size] },
          m: 2,
          borderRadius: '8px',
          boxShadow: PAPER_SHADOW,
        },
      }}
    >
      <div className="relative">
        <IconButton
          icon="Close"
          variant="minimal"
          size="sm"
          aria-label={closeLabel}
          onClick={onClose}
          className="absolute right-4 top-4 z-10 size-6! border-0! p-0! text-gray-small hover:text-black-heading [&_svg]:size-3!"
        />
        <div className="px-5 pb-6 pt-5 sm:px-6 sm:pb-7 sm:pt-6">
          {/* The title is 17px regular, between the type scale's steps and lighter than Heading's medium. */}
          <Heading
            tag="h2"
            size="lg"
            className="m-0! pr-8 text-[17px]! font-normal! leading-6! text-black-heading"
          >
            <span id={titleId}>{title}</span>
          </Heading>
          <Body
            tag="p"
            size="sm"
            className="m-0! mt-1.5! text-pretty leading-5! text-gray-small"
          >
            {description}
          </Body>
        </div>
        <div className="px-5 pb-5 sm:px-6">{children}</div>
        <div
          className={clsx(
            'flex flex-col-reverse gap-1 px-5 pb-4 pt-1 sm:flex-row sm:items-center sm:gap-2 sm:px-6',
            primaryAction ? 'sm:justify-between' : 'sm:justify-end',
          )}
        >
          <Button
            label={dismissLabel}
            variant={primaryAction ? 'text' : 'secondary'}
            size="sm"
            onClick={onDismiss}
            className={clsx(
              'h-9 w-full sm:h-7 sm:w-auto',
              primaryAction && 'sm:-ml-2',
            )}
          />
          {primaryAction && (
            <Button
              label={primaryAction.label}
              variant="primary"
              size="sm"
              onClick={primaryAction.onClick}
              className="h-9 w-full sm:h-7 sm:w-auto"
            />
          )}
        </div>
      </div>
    </MUIDialog>
  );
};
